var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { Component, Prop, Vue } from 'vue-property-decorator';
import ExpansionPanelDialog from '@/components/dialog/ExpansionPanelDialog/ExpansionPanelDialog.vue';
import { RpcClient } from '@/http';
import { SkillCategory, SkillCategoryIncludes } from '@/models/SkillCategory';
import ErrorHandler from '@/support/ErrorHandler';
import AddSkillSetDialog from '@/views/Groups/Tabs/StudentList/Dialogs/AssignSkillSetDialog/AddSkillSetDialog/AddSkillSetDialog.vue';
import { Portfolio } from '@/models/Portfolio';
let AssignSkillSetDialog = class AssignSkillSetDialog extends Vue {
    constructor() {
        // #region @VModel
        // #endregion
        super(...arguments);
        // #endregion
        // #region @Refs
        // #endregion
        // #region Class properties
        this.skillSets = [];
        this.isLoading = true;
        this.isAddSkillSetDialogVisible = false;
        this.tablePagination = {
            rowsPerPage: 10,
        };
        this.tableItems = [];
        this.selectedStudent = null;
        this.selectedSkillSet = null;
        this.selectedSkill = null;
        // #endregion
        // #region @Watchers
        // #endregion
    }
    // #endregion
    // #region Lifecycle Hooks
    mounted() {
        this.initialize();
    }
    async initialize() {
        this.isLoading = true;
        await Promise.all([await this.fetchSkillSetCategories(), await this.studentSkillSets()]);
        this.isLoading = false;
    }
    // #endregion
    // #region Event handlers
    onClose() {
        this.$emit('close');
    }
    onAssignSkillSet(item) {
        this.selectedStudent = item;
        this.isAddSkillSetDialogVisible = true;
    }
    onCloseAddSkillSetDialog() {
        this.selectedStudent = null;
        this.isAddSkillSetDialogVisible = false;
    }
    onSkillSetSelected(payload) {
        this.isAddSkillSetDialogVisible = false;
        if (this.selectedStudent === null) {
            this.addSkillSetForAllStudents(payload);
        }
        else {
            this.addSkillSetForStudent(payload);
            this.selectedStudent = null;
        }
    }
    onDeleteSkillSetFromUser(user, skillCategory) {
        this.selectedStudent = user;
        this.selectedSkillSet = skillCategory;
        this.$store.dispatch('openDialog', this.deleteSkillSetDialogOptions);
    }
    // #endregion
    // #region Class methods
    canRate(skill, number) {
        if (!skill.rating) {
            return true;
        }
        const currentUserRatings = skill.rating.find((rating) => {
            return (rating.user && rating.user.id === this.$store.state.Auth.id);
        });
        if (!currentUserRatings) {
            return true;
        }
        else {
            return currentUserRatings.rating !== number;
        }
    }
    getRatingsByNumber(skill, number) {
        if (!skill.rating) {
            return [];
        }
        return skill.rating.filter((rating) => {
            return rating.rating === number;
        });
    }
    setTableItems(data) {
        this.tableItems = Object.keys(data.users).map((key) => {
            const user = data.users[key];
            const skillCategories = user.skill_categories && typeof user.skill_categories === 'object' ? user.skill_categories : {};
            return {
                id: key,
                portfolio: user.portfolio,
                name: user.name,
                skill_categories: Object.keys(skillCategories).length
                    ? Object.keys(skillCategories).map((categoryKey) => {
                        const category = skillCategories[categoryKey];
                        const skills = category.skills && typeof category.skills === 'object' ? category.skills : {};
                        return {
                            id: categoryKey,
                            name: category.name,
                            skills: Object.keys(skills).map((skillKey) => {
                                const skill = skills[skillKey];
                                return {
                                    id: skillKey,
                                    name: skill.name,
                                    rating: skill.rating,
                                };
                            }),
                        };
                    })
                    : [],
            };
        });
    }
    // #endregion
    // #region Async methods (api calls only)]
    async onUpdateSkillRating(user, skill, number) {
        const portfolio = new Portfolio({ id: user.portfolio });
        try {
            await portfolio.updateRelation(user.portfolio, 'rate', skill.id, { rating: number });
            await this.studentSkillSets();
        }
        catch (error) {
            ErrorHandler.network(error);
        }
    }
    async onRemoveUserFromRating(portfolio, skill, ratingUser) {
        try {
            const newPortfolio = new Portfolio({ id: portfolio });
            await newPortfolio.updateRelation(portfolio, 'rate', skill.id, { rating: null, ...ratingUser ? { teacher_id: ratingUser.user.id } : {} });
            this.selectedStudent = null;
            this.selectedSkill = null;
            await this.studentSkillSets();
        }
        catch (error) {
            ErrorHandler.network(error);
        }
    }
    async addSkillSetForAllStudents(payload) {
        if (!this.selectedStudent) {
            return;
        }
        this.isLoading = true;
        try {
            await new RpcClient().execute('add-skillset-to-users', {
                users: this.students.map((student) => student.id),
                skillset: payload.skillSet,
            });
            await this.studentSkillSets();
        }
        catch (error) {
            ErrorHandler.network(error);
        }
        finally {
            this.isLoading = false;
        }
    }
    async addSkillSetForStudent(payload) {
        if (!this.selectedStudent) {
            return;
        }
        this.isLoading = true;
        try {
            await new RpcClient().execute('add-skillset-to-users', {
                users: [this.selectedStudent.id],
                skillset: payload.skillSet,
            });
            await this.studentSkillSets();
        }
        catch (error) {
            ErrorHandler.network(error);
        }
        finally {
            this.isLoading = false;
        }
    }
    async fetchSkillSetCategories() {
        try {
            const skillSets = await new SkillCategory().include([SkillCategoryIncludes.SKILLS]).all();
            this.skillSets = skillSets.filter((skillSet) => {
                return !!(skillSet.skills && skillSet.skills.length);
            });
        }
        catch (error) {
            ErrorHandler.network(error);
        }
    }
    async studentSkillSets() {
        try {
            const response = await new RpcClient().execute('get-skillset-users', {
                users: this.students.map((student) => student.id),
            });
            this.setTableItems(response);
        }
        catch (error) {
            ErrorHandler.network(error);
        }
    }
    async deleteSkillSetFromUser() {
        if (!this.selectedStudent || !this.selectedSkillSet) {
            return;
        }
        try {
            await new Portfolio().deleteRelationWithPayload(this.selectedStudent.portfolio, 'skills/delete-from-category', { category: (this.selectedSkillSet.id) });
            await this.studentSkillSets();
        }
        catch (error) {
            ErrorHandler.network(error);
        }
    }
    // #endregion
    // #region Getters & Setters
    get skillHeaders() {
        return [
            {
                text: this.$tc('Name'),
                align: 'left',
                sortable: false,
                value: 'name',
            },
            {
                text: this.$tc('Rating'),
                align: 'left',
                sortable: false,
                value: 'rating',
            },
            {
                text: this.$tc('Actions'),
                align: 'left',
                sortable: false,
                value: 'actions',
            },
        ];
    }
    get skillSetHeaders() {
        return [
            {
                text: this.$tc('Skill set'),
                align: 'left',
                sortable: false,
                value: 'name',
            },
            {
                text: '',
                align: 'left',
                sortable: false,
                value: 'chevron',
            },
        ];
    }
    get tableHeaders() {
        return [
            {
                text: this.$tc('Student'),
                align: 'left',
                sortable: false,
                value: 'name',
            },
            {
                text: '',
                align: 'left',
                sortable: false,
                value: 'chevron',
            },
        ];
    }
    get expansionPanelOptions() {
        return {
            title: this.$tc('Manage skill sets'),
            description: this.$tc('Assign skill sets to students and/or edit existing skill ratings.'),
            panels: [
                {
                    label: '',
                    slotName: 'panel1',
                    isOpen: true,
                    disabled: false,
                },
            ],
            hasSubmitButton: false,
            secondaryButtonText: this.$tc('Close'),
        };
    }
    get deleteSkillSetDialogOptions() {
        return {
            title: this.$tc('dialogOptions.confirmation'),
            text: this.$tc('Are you sure you want to delete this skill set from the user?'),
            type: 'warning',
            buttons: {
                cancel: {
                    text: this.$tc('dialogOptions.button.cancel'),
                    action: () => {
                        this.selectedStudent = null;
                        this.selectedSkillSet = null;
                    }
                },
                confirm: {
                    text: this.$t('dialogOptions.button.delete').toString(),
                    action: async () => {
                        await this.deleteSkillSetFromUser();
                        this.selectedStudent = null;
                        this.selectedSkillSet = null;
                    }
                },
            },
        };
    }
    get user() {
        return this.$store.state.Auth;
    }
};
__decorate([
    Prop({ required: true }),
    __metadata("design:type", Array)
], AssignSkillSetDialog.prototype, "students", void 0);
AssignSkillSetDialog = __decorate([
    Component({
        components: {
            ExpansionPanelDialog,
            AddSkillSetDialog,
        },
    })
], AssignSkillSetDialog);
export default AssignSkillSetDialog;
// #endregion
// #region Constants
// #endregion
